import React, { useEffect, useState, useCallback } from 'react';
import api, { formatRupiah } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { ChartLineUp, Users, Coffee, Wallet, TrendUp, UserCircle, Copy, ArrowSquareOut } from '@phosphor-icons/react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { toast } from 'sonner';

export default function DashOverview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/dashboard/stats');
      setStats(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyLink = () => {
    const link = `${window.location.origin}/u/${user?.username}`;
    navigator.clipboard.writeText(link);
    toast.success('Link profil berhasil disalin!');
  };

  if (loading) {
    return (
      <div className="border-2 border-foreground bg-white p-8 shadow-brutal-md animate-pulse">
        <p className="font-bold uppercase tracking-widest text-center">Memuat Data...</p>
      </div>
    );
  }

  const series = stats?.series_30d || [];
  const cards = [
    { label: 'Saldo Tersedia', value: formatRupiah(stats?.balance || 0), icon: Wallet, bg: 'bg-primary' },
    { label: 'Total Pendapatan', value: formatRupiah(stats?.total_earned || 0), icon: TrendUp, bg: 'bg-secondary' },
    { label: 'Total Traktiran', value: (stats?.total_traktiran || 0).toLocaleString('id-ID'), icon: Coffee, bg: 'bg-accent' },
    { label: 'Total Supporter', value: (stats?.total_supporters || 0).toLocaleString('id-ID'), icon: Users, bg: 'bg-white' },
  ];

  const publicLink = `${window.location.origin}/u/${user?.username}`;

  return (
    <div className="space-y-6 pt-2 md:pt-0 w-full overflow-x-hidden pb-10" data-testid="dash-overview">
      {/* Profile Section - Quick Access */}
      <div className="border-2 border-foreground bg-white p-4 shadow-brutal-sm flex flex-col md:flex-row items-center justify-between gap-4 mx-1">
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="w-12 h-12 border-2 border-foreground bg-secondary flex items-center justify-center shrink-0">
            <UserCircle size={32} weight="bold" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase opacity-60">Halaman Publikmu</p>
            <p className="font-mono text-sm font-bold truncate text-primary underline underline-offset-2">{publicLink}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          <button 
            onClick={copyLink}
            className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 border-2 border-foreground bg-white px-3 py-2 text-[10px] font-bold uppercase shadow-brutal-sm active:shadow-none translate-y-[-2px]"
          >
            <Copy size={16} weight="bold" /> Salin Link
          </button>
          <Link 
            to={`/u/${user?.username}`}
            className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 border-2 border-foreground bg-primary text-white px-3 py-2 text-[10px] font-bold uppercase shadow-brutal-sm active:shadow-none translate-y-[-2px]"
          >
            <ArrowSquareOut size={16} weight="bold" /> Tampilkan Profil
          </Link>
        </div>
      </div>

      {/* Stat Cards Grid - 2 columns on mobile, 4 on desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 px-1">
        {cards.map((c, i) => (
          <div 
            key={i} 
            className={`border-2 border-foreground ${c.bg} p-3 md:p-4 shadow-brutal-sm flex flex-col justify-center min-h-[90px] md:min-h-[100px] transition-transform active:scale-95`}
          >
            <div className="flex items-center justify-between mb-1 md:mb-2">
              <p className="text-[9px] md:text-[10px] uppercase tracking-widest font-black opacity-70">{c.label}</p>
              <c.icon size={16} weight="bold" />
            </div>
            <p className="font-display text-lg md:text-2xl lg:text-3xl font-bold leading-tight break-all">
              {c.value}
            </p>
          </div>
        ))}
      </div>

      {/* Chart Section - Fixed Width for Mobile */}
      <div className="border-2 border-foreground bg-white shadow-brutal-md p-4 md:p-6 mx-1 overflow-hidden" data-testid="chart-card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-black opacity-50 italic">Analisis 30 Hari</p>
            <h2 className="font-display text-2xl font-bold tracking-tight">Grafik Harian</h2>
          </div>
          <ChartLineUp size={24} weight="duotone" className="opacity-30" />
        </div>
        
        <div className="h-[250px] md:h-[300px] w-full -ml-3">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 9, fontWeight: 700 }}
                tickFormatter={(d) => d.slice(5)}
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 9, fontWeight: 700 }} 
                axisLine={false} 
                tickLine={false}
                tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} 
              />
              <Tooltip
                cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                contentStyle={{ 
                  border: '2px solid #09090B', 
                  borderRadius: 0, 
                  background: '#fff', 
                  boxShadow: '4px 4px 0 0 #09090B',
                  fontSize: '11px',
                  fontWeight: 'bold'
                }}
                formatter={(v) => formatRupiah(v)}
              />
              <Bar dataKey="amount" fill="hsl(var(--primary))" stroke="#09090B" strokeWidth={2} radius={0} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
